import { useEffect } from "react";
import { Link } from "react-router-dom";

/**
 * Startseite: Textabschnitt für Google und Besucher (SEO-Analyse 31.10.2026, Punkt 17).
 * Preise aus /api/products (Fotobox Premium 350 €, Magic Mirror 450 €, Selbstabholung Bottrop).
 * FAQPage-Schema mit data-rh, damit deploy/prerender_heads.js es ins vorgerenderte HTML übernimmt.
 */
const FAQ = [
  ["Was kostet es, eine Fotobox in NRW zu mieten?",
    "Die Fotobox Premium mieten Sie bei uns ab 350 €, den Magic Mirror ab 450 €, jeweils bei Selbstabholung in Bottrop. Druckflatrate, Requisitenbox und Online-Galerie nach dem Event sind inklusive. Lieferung mit Aufbau ist gegen Aufpreis möglich, die Kosten sehen Sie direkt bei der Buchung."],
  ["Wo kann ich die Fotobox abholen oder liefern lassen?",
    "Sie holen die Fotobox in 46242 Bottrop ab oder lassen sie von uns liefern und aufbauen. Wir liefern im ganzen Ruhrgebiet und in NRW, zum Beispiel nach Essen, Oberhausen, Gelsenkirchen, Dortmund, Duisburg und Düsseldorf."],
  ["Kann ich das Layout der Fotos selbst gestalten?",
    "Ja. Nach der Buchung wählen Sie in Ihrem Kundenkonto aus über 180 Vorlagen für Hochzeit, Geburtstag, Firmenfeier und viele weitere Anlässe und passen Namen, Datum und Farben selbst an. Ein Layout in einem Format ist inklusive."],
  ["Muss ich sofort bezahlen?",
    "Nicht unbedingt. Liegt Ihre Feier mehr als 8 Tage in der Zukunft, können Sie jetzt buchen und später zahlen. Fällig ist der Betrag dann 7 Tage vor der Veranstaltung."],
  ["Was ist der Unterschied zwischen Fotobox und Magic Mirror?",
    "Die Fotobox Premium ist kompakt und druckt Ihre Fotos in Sekunden. Der Magic Mirror ist ein großer Fotospiegel mit 65 Zoll Touchscreen, Animationen und Unterschriftfunktion und sorgt besonders auf Hochzeiten und Galas für den Wow-Effekt."],
  ["Mieten Sie auch an Firmen für Messen und Firmenfeiern?",
    "Ja. Für Firmenfeiern, Messen und Promotion-Aktionen gibt es einen eigenen Bereich für Businesskunden mit individuellem Angebot, Branding auf den Fotos und Rechnung an Ihr Unternehmen."],
];

const STEPS = [
  ["Termin wählen", "Gerät und Datum aussuchen, freie Termine sehen Sie sofort."],
  ["Layout gestalten", "Im Kundenkonto eine Vorlage wählen und mit Namen und Datum anpassen."],
  ["Abholen oder liefern", "Abholung in Bottrop oder Lieferung mit Aufbau im Ruhrgebiet und in NRW."],
  ["Feiern", "Ihre Gäste drucken unbegrenzt Fotos, danach gibt es alle Bilder in der Online-Galerie."],
];

export default function HomeInfo() {
  useEffect(() => {
    const s = document.createElement("script");
    s.type = "application/ld+json";
    s.setAttribute("data-rh", "true");
    s.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: FAQ.map(([q, a]) => ({ "@type": "Question", name: q, acceptedAnswer: { "@type": "Answer", text: a } })),
    });
    document.head.appendChild(s);
    return () => s.remove();
  }, []);

  return (
    <section className="relative w-full bg-ink-950 py-20 md:py-28" data-testid="home-info">
      <div className="container-x max-w-5xl mx-auto">
        <h2 className="font-display text-3xl md:text-5xl text-cream tracking-tight leading-tight">
          Fotobox mieten in Bottrop, im Ruhrgebiet und in ganz NRW
        </h2>
        <div className="mt-8 grid gap-6 md:grid-cols-2 text-cream/75 leading-[1.85]">
          <p>
            Picturebox24 vermietet Fotoboxen und Magic Mirror für Hochzeiten, Geburtstage, Abibälle, Firmenfeiern und Messen.
            Jedes Gerät arbeitet mit Canon-Spiegelreflexkamera und professionellem Fotodrucker, Ihre Gäste halten ihr Foto
            nach wenigen Sekunden in der Hand. Druckflatrate, Requisitenbox und Online-Galerie sind immer dabei.
          </p>
          <p>
            Unser Sitz ist Bottrop. Sie holen das Gerät bei uns ab oder lassen es liefern und aufbauen, im ganzen Ruhrgebiet
            und in NRW. Das Fotolayout gestalten Sie nach der Buchung bequem selbst im Kundenkonto, mit Vorlagen für jeden Anlass.
          </p>
        </div>

        <div className="mt-14 grid gap-6 md:grid-cols-2">
          {[
            ["Fotobox Premium", "ab 350 €", "Kompakt, schneller Sofortdruck, ideal für Feiern jeder Größe.", "/buchen?product=fotobox-premium"],
            ["Magic Mirror", "ab 450 €", "Fotospiegel mit 65 Zoll Touchscreen, Animationen und Unterschrift.", "/buchen?product=magic-mirror"],
          ].map(([name, price, text, to]) => (
            <Link key={name} to={to} className="block border border-cream/10 p-8 hover:border-gold/50 transition-colors">
              <h3 className="font-display text-2xl text-cream">{name}</h3>
              <p className="mt-2 text-gold text-xl">{price}</p>
              <p className="mt-3 text-cream/70">{text}</p>
              <p className="mt-2 text-cream/60 text-sm">Preis bei Selbstabholung in Bottrop, inklusive Druckflatrate</p>
            </Link>
          ))}
        </div>

        <h2 className="mt-20 font-display text-3xl md:text-4xl text-cream tracking-tight">So einfach geht's</h2>
        <ol className="mt-8 grid gap-6 md:grid-cols-4">
          {STEPS.map(([t, d], i) => (
            <li key={t} className="border-t border-gold/40 pt-5">
              <span className="text-gold text-sm tracking-[0.3em]">0{i + 1}</span>
              <h3 className="mt-2 text-cream text-lg font-medium">{t}</h3>
              <p className="mt-2 text-cream/70 text-[0.95rem] leading-relaxed">{d}</p>
            </li>
          ))}
        </ol>

        <h2 className="mt-20 font-display text-3xl md:text-4xl text-cream tracking-tight">Häufige Fragen</h2>
        <div className="mt-8 divide-y divide-cream/10 border-y border-cream/10">
          {FAQ.map(([q, a]) => (
            <details key={q} className="group py-5">
              <summary className="cursor-pointer list-none flex justify-between gap-4 text-cream text-lg min-h-[24px]">
                <h3 className="font-medium text-lg">{q}</h3>
                <span className="text-gold group-open:rotate-45 transition-transform" aria-hidden="true">+</span>
              </summary>
              <p className="mt-3 text-cream/75 leading-relaxed">{a}</p>
            </details>
          ))}
        </div>

        <p className="mt-14 text-cream/60 text-sm">
          Sie möchten eine Fotobox kaufen statt mieten? Das geht in unserem Shop{" "}
          <a href="https://fotoboxverkauf.de" className="text-gold underline underline-offset-4">fotoboxverkauf.de</a>.
        </p>
      </div>
    </section>
  );
}
